import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { getAssetTransferEventByUuid } from "services";

interface AuctionEventDetailProps {
  uuid: string;
}

const SellerAuctionEventDetail: React.FC<AuctionEventDetailProps> = ({ uuid }) => {
  const router = useRouter();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!uuid) return;

      setLoading(true);
      setError(null);

      try {
        const res: any = await getAssetTransferEventByUuid(uuid);
        if (!cancelled) {
          setData(res?.data?.[0] || res?.data || res);
        }
      } catch (e) {
        console.error("getAssetTransferEventByUuid failed", e);
        if (!cancelled) {
          setError("Could not load auction event details. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-sm text-[#777]">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={() => router.push("/auction-events")}
          className="mt-4 text-sm text-[#f5761a] hover:underline"
        >
          ← Back to Auction Events
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4">
        <p className="text-sm text-[#777]">No data found</p>
        <button
          onClick={() => router.push("/auction-events")}
          className="mt-4 text-sm text-[#f5761a] hover:underline"
        >
          ← Back to Auction Events
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 lg:px-8 py-6">
      <div className="mb-4">
        <button
          onClick={() => router.push("/auction-events")}
          className="text-sm text-[#f5761a] hover:underline"
        >
          ← Back to Auction Events
        </button>
      </div>

      <div className="bg-white border-2 border-gray-100 rounded-xl p-6">
        <h2 className="text-lg font-openSans font-normal text-bankerColor mb-6">
          Auction Event Details
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs text-[#777] mb-1">Event ID</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.event_id || data?.uuid || "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#777] mb-1">Status</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.status || "—"}
            </p>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs text-[#777] mb-1">Title</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.title || data?.secured_asset_description || "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#777] mb-1">Reserve Price</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.reserve_price ? `₹ ${data.reserve_price}` : "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#777] mb-1">EMD Amount</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.emd_amount ? `₹ ${data.emd_amount}` : "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#777] mb-1">Start Time</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.start_time || "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#777] mb-1">End Time</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.end_time || "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#777] mb-1">Lender Name</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.lender_name || "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#777] mb-1">Asset Category</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.asset_category || "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#777] mb-1">Type</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.type || "—"}
            </p>
          </div>

          <div>
            <p className="text-xs text-[#777] mb-1">Currency</p>
            <p className="text-sm font-semibold text-[#111]">
              {data?.currency || "INR"}
            </p>
          </div>
        </div>

        {data?.meta && typeof data.meta === "object" && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-[#111] mb-3">Additional Details</h3>
            <pre className="bg-gray-50 p-4 rounded-lg text-xs overflow-auto">
              {JSON.stringify(data.meta, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerAuctionEventDetail;